/**
 * O painel de diagnóstico e o gesto que o abre — extraídos do `BootScreen`
 * para serem reusados por QUALQUER tela que possa pendurar.
 *
 * ── Por que virou módulo (20/08/2026) ───────────────────────────────────────
 *
 * Nasceu privado no `BootScreen` porque só o boot pendurava. A tela de PRO no
 * iOS mostrou que não: ela fica em "Buscando os planos na loja…" sem teto, e no
 * TestFlight **não havia nenhum canal para ler o que ela está fazendo** — os
 * `console.warn` do cliente da RevenueCat só aparecem com um Mac e o Safari Web
 * Inspector ligados. Uma segunda cópia do painel divergiria da primeira; o
 * caminho é um lugar só.
 *
 * ⚠️ Este arquivo mantém a disciplina do `BootScreen`: os `<button>` são raw
 * (DS-drift assumido) e não há ícone, portal, contexto nem hook de tema. Quanto
 * menos o diagnóstico importar, menor a chance de ele falhar junto com o que
 * está sendo diagnosticado.
 */

import { useState } from "react";
import { formatBootLog } from "../../lib/boot/bootTrace";
import { copyDiagnosticText, useDiagnosticLog } from "./diagnosticLogHooks";

/**
 * O painel. `title` existe porque ele deixou de ser só do boot — quem abre pela
 * tela de PRO precisa saber que está olhando o log certo.
 */
export function DiagnosticLogPanel({
  onClose,
  title = "Log de abertura",
}: {
  onClose: () => void;
  title?: string;
}) {
  const log = useDiagnosticLog();
  const [copied, setCopied] = useState(false);
  const text = formatBootLog(log);

  // A cópia vive em `copyDiagnosticText` porque o card de erro da vitrine
  // precisa da MESMA — duas transcrições divergiriam no dia em que o formato do
  // log mudasse. Aqui a falha pode ser silenciosa: o texto segue na tela e o
  // print resolve, que é o caminho principal deste painel de qualquer forma.
  const copy = async () => {
    setCopied(await copyDiagnosticText());
  };

  return (
    // `pt-[calc(...)]`: o painel é `fixed inset-0`, então NÃO herda o
    // `padding-top: var(--sat)` do body (a autoridade única cobre o fluxo do
    // documento, não overlays) — sem a reserva própria, título e botões nascem
    // POR BAIXO do relógio/bateria em device com notch (classe BUG-130/195,
    // relatada aqui em 01/09). Mesmo padrão do FocusModeModal.
    <div className="fixed inset-0 z-[100] flex flex-col bg-background p-4 pt-[calc(16px+env(safe-area-inset-top,0px))]">
      <div className="flex items-center justify-between gap-3">
        <p className="font-display text-title-sm font-semibold text-ink">{title}</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void copy()}
            className="min-h-11 rounded-control border border-line px-3 text-body-sm font-medium text-ink"
          >
            {copied ? "Copiado" : "Copiar"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-control border border-line px-3 text-body-sm font-medium text-ink"
          >
            Fechar
          </button>
        </div>
      </div>
      {/*
        `whitespace-pre-wrap` + `break-words` NÃO são estética.

        Medido no render: com o `<pre>` rolando na horizontal, a linha mais
        importante do diagnóstico — `firebase-init — authDomain=…` — saía
        CORTADA na borda direita. Este painel existe para virar print; o que
        não cabe na largura simplesmente não chega até nós. Rolagem horizontal
        é uma resposta razoável para código-fonte e é a resposta errada para
        um artefato que a pessoa fotografa.
      */}
      <pre className="mt-3 flex-1 overflow-y-auto rounded-card bg-surface-alt p-3 text-[11px] leading-relaxed whitespace-pre-wrap break-words text-ink">
        {text || "Sem etapas registradas."}
      </pre>
    </div>
  );
}
